import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * SIZE GATE
 * ---------
 * A shopper must choose a size before anything reaches the cart, but the
 * button does NOT advertise that up front. It sits in its normal green
 * "Add to cart" state and only turns red with "Please select a size" once
 * someone actually clicks it without choosing. Picking a size clears that
 * state again.
 *
 * Three things enforce it:
 *   1. handleClick   - swallows the click so the success animation never runs
 *   2. handleSubmit  - refuses the submit and flips on data-size-missing
 *   3. variantId     - forced back to '' whenever no size is chosen, so even
 *                      a submit that slipped through would carry no variant
 *
 * Express checkout posts through Shopify's own script and cannot be
 * intercepted here, so it stays hidden until a size resolves a variant.
 *
 * Presentation for the refused state lives in assets/size-gate.css, keyed
 * off data-size-missing. This file sets state, never styles - and it hands
 * the prompt to CSS as a custom property rather than writing it into the
 * button, because every variant change morphs the button from fresh server
 * HTML and would wipe text written here.
 */
const SIZE_PROMPT_FALLBACK = 'Please select a size';
const SIZE_GROUP_SELECTOR = '.variant-option[data-option-name]';
const SIZE_GROUP_GATED_SELECTOR = '.variant-option[data-is-size-option="true"]';

/**
 * SIZE GATE: the variant named in the URL of the ACTUAL request, or null.
 *
 * Read once at import, before the page can rewrite its own address bar.
 * Every picker interaction pushes a new `?variant=` into the URL, so
 * reading location.search later could not tell a shared link from a click
 * the shopper just made two seconds ago. This can.
 * @type {string | null}
 */
const INITIAL_VARIANT_PARAM = new URLSearchParams(window.location.search).get('variant');

/**
 * The shopper-facing prompt, from the theme's translations when wired up.
 * @returns {string}
 */
function sizePromptText() {
  return Theme?.translations?.select_size_prompt || Theme?.translations?.select_size || SIZE_PROMPT_FALLBACK;
}

/**
 * The default add to cart label.
 * @returns {string}
 */
function addToCartText() {
  return Theme?.translations?.add_to_cart || 'Add to cart';
}

/**
 * Whether the given size group has a value chosen, in either markup style.
 * @param {Element | null | undefined} sizeGroup
 * @returns {boolean}
 */
function hasSizeSelection(sizeGroup) {
  if (!sizeGroup) return false;
  const checkedRadio = sizeGroup.querySelector('input[type="radio"]:checked');
  const select = /** @type {HTMLSelectElement | null} */ (sizeGroup.querySelector('select.variant-option__select'));
  const selectHasValue = select ? select.value !== '' : false;
  return Boolean(checkedRadio || selectHasValue);
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // SIZE GATE: a refused click must not play the "Added" animation or fly
    // an image to the cart. handleSubmit does the refusing and the messaging;
    // this only stops the celebration that fires on click, before the form
    // has submitted anything.
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    if (productForm?.isSizeMissing()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    this.#applyDeepLinkedSize();
    this.#updateAddToCartStateForSize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * SIZE GATE: the size control for this form, or null when the product has
   * no size option at all.
   * @returns {Element | null}
   */
  get sizeGroup() {
    const root = this.closest('.shopify-section, dialog, product-card') || this;
    return root.querySelector(SIZE_GROUP_GATED_SELECTOR);
  }

  /**
   * SIZE GATE: whether this product has a size option with nothing chosen.
   * A product without a size option is never gated.
   * @returns {boolean}
   */
  isSizeMissing() {
    const sizeGroup = this.sizeGroup;
    if (!sizeGroup) return false;
    return !hasSizeSelection(sizeGroup);
  }

  /**
   * SIZE GATE: ticks the size a deep link already asked for.
   *
   * The picker deliberately never renders a Size as checked, which is what
   * makes the gate meaningful - but it also meant a link naming a variant (a
   * shared link, an ad, a collection swatch) landed with no size chosen and
   * asked the shopper to pick the size they had already been promised.
   *
   * Runs once, at connect, and only from the variant in the real request URL.
   * A sold-out variant is left alone rather than talked into.
   */
  #applyDeepLinkedSize() {
    if (!INITIAL_VARIANT_PARAM) return;
    // Quick-add modals and cards show other products; the page URL is not
    // about them.
    if (this.closest('dialog, product-card')) return;

    const sizeGroup = this.sizeGroup;
    if (!sizeGroup || hasSizeSelection(sizeGroup)) return;

    const picker = this.closest('.shopify-section')?.querySelector('variant-picker');
    const blob = picker?.querySelector('script[type="application/json"]');
    if (!blob) return;

    let variant;
    try {
      variant = JSON.parse(blob.textContent || '');
    } catch {
      return;
    }

    if (!variant || String(variant.id) !== String(INITIAL_VARIANT_PARAM)) return;
    if (variant.available === false) return;

    // The option groups render in option order, so the size group's position
    // among them is its index into the variant's own options list.
    const groups = [...(picker?.querySelectorAll(SIZE_GROUP_SELECTOR) || [])];
    const sizeValue = variant.options?.[groups.indexOf(sizeGroup)];
    if (!sizeValue) return;

    const radio = [...sizeGroup.querySelectorAll('input[type="radio"]')].find(
      (input) => input instanceof HTMLInputElement && input.value === sizeValue
    );
    const select = /** @type {HTMLSelectElement | null} */ (sizeGroup.querySelector('select.variant-option__select'));

    if (radio instanceof HTMLInputElement) {
      radio.checked = true;
    } else if (select instanceof HTMLSelectElement) {
      select.value = sizeValue;
    } else {
      return;
    }

    this.refs.variantId.value = variant.id;
  }

  /**
   * SIZE GATE: writes the add to cart label.
   *
   * Every copy of it, not just the first: the button renders the label twice
   * so it can slide one out and the other in on hover, and updating only one
   * leaves the hover state contradicting the resting state.
   * @param {string} text
   */
  setAddToCartLabel(text) {
    const container = this.refs.addToCartButtonContainer;
    if (!container) return;
    container.querySelectorAll('.add-to-cart-text__content').forEach((element) => {
      element.textContent = text;
    });
  }

  /**
   * SIZE GATE: turns the refused state on.
   *
   * The prompt is handed to CSS as a custom property instead of being written
   * into the button. Attributes and inline styles on THIS element survive a
   * variant change; the button's own text does not, because the button is
   * morphed from fresh server HTML.
   */
  showSizePrompt() {
    const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;

    // Re-toggle so the nudge in size-gate.css replays on every refused click
    // rather than only the first: an animation keyed on an attribute that
    // never changes runs once.
    if (this.dataset.sizeMissing === 'true') {
      delete this.dataset.sizeMissing;
      if (button) void button.offsetWidth;
    }

    this.style.setProperty('--size-gate-prompt', JSON.stringify(sizePromptText()));
    this.dataset.sizeMissing = 'true';
  }

  /**
   * SIZE GATE: clears the refused state.
   */
  clearSizePrompt() {
    if (this.dataset.sizeMissing !== 'true') return;
    delete this.dataset.sizeMissing;
    this.style.removeProperty('--size-gate-prompt');
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // SIZE GATE: refuse the add when the product has a size option and the
    // shopper has not chosen one. The button carries the message, so the
    // inline error stays hidden; the live region is what screen readers get.
    const sizeGroup = this.sizeGroup;

    if (sizeGroup && !hasSizeSelection(sizeGroup)) {
      this.showSizePrompt();
      this.#setLiveRegionText(sizePromptText());

      // Move the shopper to the control they need to use. On mobile the
      // button is often the only part of the form on screen.
      const firstSizeInput = sizeGroup.querySelector('input[type="radio"]');
      const sizeSelect = sizeGroup.querySelector('select.variant-option__select');
      if (firstSizeInput instanceof HTMLElement) {
        firstSizeInput.focus();
      } else if (sizeSelect instanceof HTMLElement) {
        sizeSelect.focus();
      }

      return; // Block submission
    }

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // First, morph the add to cart button to the latest HTML
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Determine if the product has a size option and whether a size is selected
    const sizeFieldset = this.sizeGroup;
    const sizeSelected = hasSizeSelection(sizeFieldset);

    // SIZE GATE: a missing size no longer disables the button - it stays
    // green and clickable, and the click itself is what gets refused. Only a
    // genuinely unavailable variant disables it here.
    if (event.detail.resource == null || event.detail.resource.available == false) {
      if (addToCartButtonContainer && typeof addToCartButtonContainer.disable === 'function') {
        addToCartButtonContainer.disable();
      }
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      if (addToCartButtonContainer && typeof addToCartButtonContainer.enable === 'function') {
        addToCartButtonContainer.enable();
      }
      const addToCartTextError = this.refs.addToCartTextError;
      if (addToCartTextError) addToCartTextError.classList.add('hidden');
    }

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    // SIZE GATE: Shopify resolves a variant from a partial option list, so a
    // colour click alone comes back with a variant for a size nobody chose.
    // Blank it, keep express checkout hidden, and put the resting label back -
    // the server may have rendered the old "Please select a size" wording, and
    // the prompt is now earned by a click, not shown by default. Any refused
    // state already on this element is left alone: the shopper still has not
    // chosen a size, so the red button is still telling the truth.
    if (sizeFieldset && !sizeSelected) {
      variantId.value = '';
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      this.#stripSizeValidation(sizeFieldset);
      this.setAddToCartLabel(addToCartText());
    } else {
      this.clearSizePrompt();
      if (event.detail.resource != null && event.detail.resource.available != false) {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
    this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    // Don't re-enable until the variant fetch completes - check is done in #updateAddToCartStateForSize
  };

  /**
   * SIZE GATE: drops the native `required` from the size radios.
   *
   * They are rendered with `required` and styled to opacity 0, which is a
   * combination the browser cannot show a validation bubble for - it refuses
   * the submit outright and logs "not focusable", so handleSubmit would never
   * run and the button would appear dead. Our own check enforces the size.
   * @param {Element | null} sizeGroup
   */
  #stripSizeValidation(sizeGroup) {
    if (!sizeGroup) return;
    sizeGroup.querySelectorAll('[required]').forEach((input) => input.removeAttribute('required'));
  }

  /**
   * Ensure initial Add to Cart state when size option exists.
   */
  #updateAddToCartStateForSize() {
    const addToCartButtonContainer = this.refs.addToCartButtonContainer;
    const { variantId } = this.refs;
    const variantPicker = this.closest('.shopify-section, dialog, product-card')?.querySelector('variant-picker');
    const sizeFieldset = variantPicker?.querySelector('[data-is-size-option="true"]');
    if (!addToCartButtonContainer || !sizeFieldset) return;

    this.#stripSizeValidation(sizeFieldset);

    // Check if variant picker is still loading - don't enable button until fetch completes
    const isLoading = variantPicker?.dataset?.variantLoading === 'true';

    const sizeSelected = hasSizeSelection(sizeFieldset);

    // SIZE GATE: with no size chosen the button is left looking normal -
    // green, labelled "Add to cart", clickable. What it must not do is carry a
    // variant or expose express checkout, because neither of those routes
    // through the click we intercept.
    if (!sizeSelected) {
      if (isLoading && typeof addToCartButtonContainer.disable === 'function') {
        addToCartButtonContainer.disable();
      } else if (typeof addToCartButtonContainer.enable === 'function') {
        addToCartButtonContainer.enable();
      }

      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      if (variantId) variantId.value = '';

      // The server renders the prompt as the label when it cannot resolve a
      // variant. That is the old behaviour; the prompt is now earned by a
      // click, so put the normal label back.
      this.setAddToCartLabel(addToCartText());
      return;
    }

    if (isLoading) {
      if (typeof addToCartButtonContainer.disable === 'function') addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      return;
    }

    if (typeof addToCartButtonContainer.enable === 'function') {
      addToCartButtonContainer.enable();
    }
    this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    this.setAddToCartLabel(addToCartText());
    this.clearSizePrompt();
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
